// Comprehensive CMS server functions backed by Supabase.
// Public reads use the admin client (service role) to safely return only published rows.
// Admin writes are gated by requireSupabaseAuth + is_active_admin check.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function adminClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Server configuration error");
  const { createClient } = await import("@supabase/supabase-js");
  return createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } });
}

type AdminRole = "super_admin" | "admin" | "editor" | "viewer";
const ROLE_RANK: Record<AdminRole, number> = { viewer: 1, editor: 2, admin: 3, super_admin: 4 };

async function assertAdmin(userId: string, minRole: AdminRole = "viewer") {
  const c = await adminClient();
  const { data } = await c.from("admin_users").select("status, role").eq("user_id", userId).maybeSingle();
  if (!data || data.status !== "active") throw new Error("Forbidden");
  const userRank = ROLE_RANK[(data.role as AdminRole) ?? "viewer"] ?? 0;
  if (userRank < ROLE_RANK[minRole]) throw new Error("Forbidden: insufficient role");
}

// ============ TESTIMONIALS ============
const testimonialInput = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(120),
  text: z.string().min(1).max(2000),
  rating: z.number().int().min(1).max(5),
  service: z.string().max(80).optional().nullable(),
  source: z.string().max(60).optional().nullable(),
  source_url: z.string().url().max(500).optional().nullable().or(z.literal("").transform(() => null)),
  pages: z.array(z.string().min(1).max(60)).max(20).default(["all"]),
  sort_order: z.number().int().default(0),
  status: z.enum(["published", "draft", "archived"]).default("published"),
});

export const listTestimonials = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data, error } = await c.from("testimonials").select("*").eq("status", "published").order("sort_order");
  if (error) return { items: [] };
  return { items: data ?? [] };
});

export const adminListTestimonials = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("testimonials").select("*").order("sort_order");
    return { items: data ?? [] };
  });

export const upsertTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => testimonialInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("testimonials").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.from("testimonials").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ AREAS ============
const areaInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  hero_copy: z.string().max(2000).optional().nullable(),
  postcodes: z.array(z.string().max(20)).default([]),
  meta_title: z.string().max(200).optional().nullable(),
  meta_description: z.string().max(400).optional().nullable(),
  sort_order: z.number().int().default(0),
  status: z.enum(["published", "draft"]).default("published"),
});

export const listAreas = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("service_areas").select("*").eq("status", "published").order("sort_order");
  return { items: data ?? [] };
});

export const adminListAreas = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("service_areas").select("*").order("sort_order");
    return { items: data ?? [] };
  });

export const upsertArea = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => areaInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("service_areas").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteArea = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.from("service_areas").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ SERVICES ============
const serviceInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  short_desc: z.string().max(500).optional().nullable(),
  icon: z.string().max(60).optional().nullable(),
  meta_title: z.string().max(200).optional().nullable(),
  meta_description: z.string().max(400).optional().nullable(),
  sort_order: z.number().int().default(0),
  status: z.enum(["published", "draft"]).default("published"),
});

export const adminListServices = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("service_categories").select("*").order("sort_order");
    return { items: data ?? [] };
  });

export const upsertService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => serviceInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("service_categories").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============ BUSINESS INFO (NAP) ============
export const getBusinessInfo = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("business_info").select("key,value");
  const values: Record<string, unknown> = {};
  for (const row of data ?? []) values[row.key] = row.value;
  return { values: values as Record<string, string | number | boolean | object | null> };
});

export const upsertBusinessInfo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({
    key: z.string().min(1).max(100),
    value: z.unknown(),
  }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    const { error } = await c.from("business_info").upsert({
      key: data.key,
      value: data.value as never,
      updated_at: new Date().toISOString(),
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============ BLOG ============
const blogInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(200),
  title: z.string().min(1).max(300),
  excerpt: z.string().max(500).optional().nullable(),
  content: z.string().default(""),
  cover_image_url: z.string().url().optional().nullable().or(z.literal("").transform(() => null)),
  author: z.string().max(120).optional().nullable(),
  tags: z.array(z.string().max(60)).default([]),
  meta_title: z.string().max(200).optional().nullable(),
  meta_description: z.string().max(400).optional().nullable(),
  status: z.enum(["published", "draft", "archived"]).default("draft"),
  published_at: z.string().optional().nullable(),
});

export const listBlogPosts = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c
    .from("blog_posts")
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  return { items: data ?? [] };
});

export const adminListBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("blog_posts").select("*").order("created_at", { ascending: false });
    return { items: data ?? [] };
  });

export const upsertBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => blogInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const payload = { ...data, published_at: data.status === "published" ? (data.published_at ?? new Date().toISOString()) : null };
    const { error } = await c.from("blog_posts").upsert(payload);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.from("blog_posts").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ REDIRECTS ============
const redirectInput = z.object({
  id: z.string().uuid().optional(),
  from_path: z.string().min(1).max(500),
  to_path: z.string().min(1).max(500),
  status_code: z.number().int().refine((n) => [301, 302, 307, 308].includes(n)).default(301),
  is_active: z.boolean().default(true),
  notes: z.string().max(500).optional().nullable(),
});

export const adminListRedirects = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("redirects").select("*").order("from_path");
    return { items: data ?? [] };
  });

export const listActiveRedirects = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("redirects").select("from_path,to_path,status_code").eq("is_active", true);
  return { items: data ?? [] };
});

export const upsertRedirect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => redirectInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("redirects").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteRedirect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.from("redirects").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ SITEMAP ============
const sitemapInput = z.object({
  id: z.string().uuid().optional(),
  path: z.string().min(1).max(500),
  priority: z.number().min(0).max(1).default(0.5),
  changefreq: z.enum(["always", "hourly", "daily", "weekly", "monthly", "yearly", "never"]).default("monthly"),
  included: z.boolean().default(true),
});

export const listSitemapEntries = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("sitemap_entries").select("*").eq("included", true).order("priority", { ascending: false });
  return { items: data ?? [] };
});

export const adminListSitemap = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("sitemap_entries").select("*").order("path");
    return { items: data ?? [] };
  });

export const upsertSitemapEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => sitemapInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("sitemap_entries").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============ CMS SECTIONS (per page) ============
const sectionInput = z.object({
  id: z.string().uuid().optional(),
  page_slug: z.string().min(1).max(100),
  section_key: z.string().min(1).max(100),
  type: z.string().max(60).default("rich_text"),
  sort_order: z.number().int().default(0),
  status: z.enum(["published", "draft"]).default("published"),
  content: z.object({
    heading: z.string().max(300).optional(),
    body: z.string().max(5000).optional(),
    cta_label: z.string().max(100).optional(),
    cta_href: z
      .string()
      .max(500)
      .optional()
      .refine(
        (v) => !v || /^(https?:\/\/|\/|mailto:|tel:)/i.test(v),
        "CTA href must be an http(s), mailto, tel, or relative URL",
      ),
  }).passthrough().default({}),
});

export const listPageSections = createServerFn({ method: "GET" })
  .validator((i: unknown) => z.object({ pageSlug: z.string().min(1).max(100) }).parse(i))
  .handler(async ({ data }) => {
    const c = await adminClient();
    const { data: rows } = await c
      .from("cms_sections")
      .select("*")
      .eq("page_slug", data.pageSlug)
      .eq("status", "published")
      .order("sort_order");
    return { items: rows ?? [] };
  });

export const adminListPageSections = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ pageSlug: z.string().min(1).max(100) }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data: rows } = await c
      .from("cms_sections")
      .select("*")
      .eq("page_slug", data.pageSlug)
      .order("sort_order");
    return { items: rows ?? [] };
  });

export const upsertSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => sectionInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("cms_sections").upsert(data);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.from("cms_sections").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ LAUNCH CHECKLIST ============
const checklistItem = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(300),
  group: z.string().min(1).max(60),
  confirmed: z.boolean(),
});

export const getLaunchChecklist = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("business_info").select("value").eq("key", "launch_checklist").maybeSingle();
  const items = ((data?.value as { items?: unknown })?.items as unknown[]) ?? [];
  return { items: items as { id: string; label: string; group: string; confirmed: boolean }[] };
});

export const saveLaunchChecklist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ items: z.array(checklistItem).max(60) }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("business_info").upsert({
      key: "launch_checklist",
      value: { items: data.items } as never,
      description: "Pre-publish launch checklist",
      updated_at: new Date().toISOString(),
    }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============ PUBLISH STATUS (gated by launch checklist) ============
const CRITICAL_KEYS = ["address", "postcode", "hours", "mot-classes", "booking", "whatsapp", "collection"] as const;

async function readChecklist() {
  const c = await adminClient();
  const { data } = await c.from("business_info").select("value").eq("key", "launch_checklist").maybeSingle();
  const items = ((data?.value as { items?: unknown })?.items as { id: string; group: string; confirmed: boolean }[]) ?? [];
  return items;
}

function findCriticalGaps(items: { id: string; group: string; confirmed: boolean }[]) {
  const critical = items.filter((i) => i.group === "critical" || CRITICAL_KEYS.includes(i.id as typeof CRITICAL_KEYS[number]));
  const missingIds = CRITICAL_KEYS.filter((k) => !items.some((i) => i.id === k));
  const unconfirmed = critical.filter((i) => !i.confirmed).map((i) => i.id);
  return { unconfirmed, missingIds, ready: unconfirmed.length === 0 && missingIds.length === 0 };
}

export const getPublishStatus = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("business_info").select("value").eq("key", "publish_status").maybeSingle();
  const status = (data?.value as { mode?: string; updated_at?: string } | null) ?? { mode: "draft" };
  const items = await readChecklist();
  const gate = findCriticalGaps(items);
  return { mode: status.mode === "live" ? "live" : "draft", ...gate };
});

// Reserved section keys per page slug — must mirror DEFAULT_RESERVED in CmsSections.tsx
const RESERVED_SECTION_KEYS: Record<string, string[]> = {
  home: [
    "hero", "proof", "reassurance", "services", "service-picker", "service_picker",
    "why", "why-choose-us", "trust", "compliance", "reviews", "testimonials",
    "what-to-expect", "process", "vehicles", "vehicle-types", "gallery",
    "featured-photos", "workshop", "areas", "find-us", "directions",
    "arrival-faq", "faq", "faqs", "blog", "related-blog", "instagram",
    "compliance-block", "quick-contact", "contact", "cta", "final-cta",
  ],
};

async function findReservedSectionCollisions() {
  const c = await adminClient();
  const collisions: { page_slug: string; section_key: string; id: string }[] = [];
  for (const [slug, keys] of Object.entries(RESERVED_SECTION_KEYS)) {
    const { data } = await c
      .from("cms_sections")
      .select("id,page_slug,section_key")
      .eq("page_slug", slug)
      .eq("status", "published");
    for (const row of data ?? []) {
      if (keys.includes((row.section_key || "").toLowerCase())) collisions.push(row as never);
    }
  }
  return collisions;
}

export const setPublishStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ mode: z.enum(["live", "draft"]) }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    if (data.mode === "live") {
      const items = await readChecklist();
      const gate = findCriticalGaps(items);
      if (!gate.ready) {
        const detail = [
          gate.missingIds.length ? `missing: ${gate.missingIds.join(", ")}` : null,
          gate.unconfirmed.length ? `unconfirmed: ${gate.unconfirmed.join(", ")}` : null,
        ].filter(Boolean).join("; ");
        throw new Error(`Cannot publish — launch checklist incomplete (${detail}).`);
      }
      const collisions = await findReservedSectionCollisions();
      if (collisions.length) {
        const list = collisions.map((c) => `${c.page_slug}:${c.section_key}`).join(", ");
        throw new Error(
          `Cannot publish — ${collisions.length} CMS section(s) duplicate reserved static blocks (${list}). Unpublish or rename them in CMS: Page Sections.`
        );
      }
    }
    const c = await adminClient();
    const { error } = await c.from("business_info").upsert({
      key: "publish_status",
      value: { mode: data.mode, updated_at: new Date().toISOString() } as never,
      description: "Site-wide publish gate. Live = public publish allowed.",
      updated_at: new Date().toISOString(),
    }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    // Audit log
    await c.from("publish_logs").insert({
      action: "publish_status_change",
      entity_type: "site",
      new_status: data.mode,
      changed_by: context.userId,
      changed_by_email: context.claims?.email ?? null,
      change_summary: `Site publish mode set to ${data.mode}`,
    });
    return { ok: true };
  });


// ============ PUBLIC BLOG (single + list) ============
export const getPublicBlogPost = createServerFn({ method: "GET" })
  .validator((i: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const c = await adminClient();
    // Prefer the unified content view (Phase 7). Falls back to the legacy
    // blog_posts table so any row not yet backfilled keeps rendering.
    const { data: unified } = await c
      .from("v_published_content")
      .select("id, slug, title, excerpt, body, cover_image_url, tags, published_at, reading_minutes, meta_title, meta_description")
      .eq("content_type", "blog")
      .eq("slug", data.slug)
      .maybeSingle();
    if (unified) {
      const markdown = (unified.body as { markdown?: string } | null)?.markdown ?? "";
      return {
        post: {
          id: unified.id,
          slug: unified.slug,
          title: unified.title,
          excerpt: unified.excerpt,
          content: markdown,
          cover_image_url: unified.cover_image_url,
          tags: unified.tags ?? [],
          published_at: unified.published_at,
          reading_minutes: unified.reading_minutes,
          meta_title: unified.meta_title,
          meta_description: unified.meta_description,
        },
      };
    }
    const { data: row } = await c
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    return { post: row };
  });

// ============ MEDIA LIBRARY ============
export const adminListMedia = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const { data } = await c.from("media_library").select("*").order("created_at", { ascending: false });
    return { items: data ?? [] };
  });

export const upsertMedia = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({
    id: z.string().uuid().optional(),
    storage_path: z.string().min(1),
    public_url: z.string().url(),
    alt_text: z.string().max(300).default(""),
    caption: z.string().max(500).optional().nullable(),
    tags: z.array(z.string()).default([]),
    width: z.number().int().optional().nullable(),
    height: z.number().int().optional().nullable(),
    mime_type: z.string().optional().nullable(),
    size_bytes: z.number().int().optional().nullable(),
    is_active: z.boolean().default(true),
  }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("media_library").upsert({ ...data, uploaded_by: context.userId });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteMedia = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({ id: z.string().uuid(), storage_path: z.string() }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "admin");
    const c = await adminClient();
    await c.storage.from("media").remove([data.storage_path]);
    await c.from("media_library").delete().eq("id", data.id);
    return { ok: true };
  });

// ============ SITE HEALTH ============
export const getSiteHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const c = await adminClient();
    const [t, a, s, b, l, r] = await Promise.all([
      c.from("testimonials").select("id", { count: "exact", head: true }).eq("status", "published"),
      c.from("service_areas").select("id", { count: "exact", head: true }).eq("status", "published"),
      c.from("service_categories").select("id", { count: "exact", head: true }).eq("status", "published"),
      c.from("blog_posts").select("id", { count: "exact", head: true }).eq("status", "published"),
      c.from("leads").select("id", { count: "exact", head: true }).gte("created_at", new Date(Date.now() - 30 * 86400000).toISOString()),
      c.from("redirects").select("id", { count: "exact", head: true }).eq("is_active", true),
    ]);
    const checks = [
      { id: "reviews", label: "Published testimonials", value: t.count ?? 0, ok: (t.count ?? 0) >= 5 },
      { id: "areas", label: "Published areas", value: a.count ?? 0, ok: (a.count ?? 0) >= 6 },
      { id: "services", label: "Published services", value: s.count ?? 0, ok: (s.count ?? 0) >= 5 },
      { id: "blog", label: "Published blog posts", value: b.count ?? 0, ok: (b.count ?? 0) >= 1 },
      { id: "leads30d", label: "Leads (30d)", value: l.count ?? 0, ok: true },
      { id: "redirects", label: "Active redirects", value: r.count ?? 0, ok: true },
    ];
    const score = Math.round((checks.filter((c) => c.ok).length / checks.length) * 100);
    return { score, checks };
  });

// ============ HERO OVERLAY ============
// Stored in business_info.key='hero_overlay' as { desktop, mobile, tone }
// desktop/mobile = 0..1 darkness of left-side gradient on hero image
// tone = 'dark' (white text on dark image) | 'light' (dark text on bright image)
export type HeroOverlay = { desktop: number; mobile: number; tone: "dark" | "light" };
const DEFAULT_HERO_OVERLAY: HeroOverlay = { desktop: 0.62, mobile: 0.78, tone: "dark" };

export const getHeroOverlay = createServerFn({ method: "GET" }).handler(async () => {
  const c = await adminClient();
  const { data } = await c.from("business_info").select("value").eq("key", "hero_overlay").maybeSingle();
  const v = (data?.value as Partial<HeroOverlay> | null) ?? {};
  return {
    desktop: typeof v.desktop === "number" ? Math.min(0.95, Math.max(0, v.desktop)) : DEFAULT_HERO_OVERLAY.desktop,
    mobile: typeof v.mobile === "number" ? Math.min(0.95, Math.max(0, v.mobile)) : DEFAULT_HERO_OVERLAY.mobile,
    tone: v.tone === "light" ? "light" : "dark",
  } as HeroOverlay;
});

export const setHeroOverlay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((i: unknown) => z.object({
    desktop: z.number().min(0).max(0.95),
    mobile: z.number().min(0).max(0.95),
    tone: z.enum(["dark", "light"]),
  }).parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "editor");
    const c = await adminClient();
    const { error } = await c.from("business_info").upsert({
      key: "hero_overlay",
      value: data as never,
      description: "Homepage hero gradient opacity (0..0.95) and text tone",
      updated_at: new Date().toISOString(),
    }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
